import { useEffect } from 'react';
import { useFela } from 'react-fela';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useSyncRef } from '@/hooks/useSyncRef';
import type { Vault } from '@/modules/api/codegen';
import { FormField, Label, TextInput, useFormSubmit } from '@/modules/forms';
import { Modal } from '@/modules/modals';
import { Heading } from '@/modules/ui';

import { VaultFormField } from '../../constants';
import type { VaultFormValues } from '../../schemas';
import { hasVaultEditor, vaultToFormValues } from '../../utils';
import * as felaRules from './VaultForm.rules';
import { VaultGroupsField } from './VaultGroupsField';
import { VaultUsersField } from './VaultUsersField';

const MODAL_WIDTH = 520;

export interface VaultFormProps {
    open: boolean;
    /** Absent means create. */
    vault?: Vault;
    onClose: () => void;
}

export const VaultForm = ({ open, vault, onClose }: VaultFormProps) => {
    const { css } = useFela();
    const { formatMessage } = useIntl();

    const submit = useFormSubmit();
    const { reset } = useFormContext<VaultFormValues>();
    const { isValid, isDirty, isSubmitting } = useFormState<VaultFormValues>();

    const groups = useWatch<VaultFormValues, VaultFormField.GROUPS>({ name: VaultFormField.GROUPS });
    const users = useWatch<VaultFormValues, VaultFormField.USERS>({ name: VaultFormField.USERS });

    // Read through a ref so a vault-list refetch, which hands down a new object for the same vault,
    // cannot wipe edits in progress. Only opening resets.
    const vaultRef = useSyncRef(vault);

    // The modal stays mounted so it keeps its transition, which means the form outlives a close. Reopening
    // must therefore show the stored vault again rather than the edits that were abandoned.
    useEffect(() => {
        if (open) {
            reset(vaultToFormValues(vaultRef.current));
        }
    }, [open, reset, vaultRef]);

    const isEditing = Boolean(vault);

    // Creating persists the caller as an editor whatever the form says, so the rule only binds on edit.
    // The server rejects it too — that backstop matters because an editor can arrive through a group the
    // client cannot see.
    const missingEditor = isEditing && !hasVaultEditor({ groups, users });

    return (
        <Modal
            open={open}
            closable
            alignButtonsRight
            width={MODAL_WIDTH}
            onClose={onClose}
            extend={{ buttons: felaRules.buttons }}
            cancelButton={{
                onClick: onClose,
                disabled: isSubmitting,
                children: formatMessage({ id: 'vaults.form.cancel' }),
                'data-testid': 'close-vault-form-modal',
            }}
            okButton={{
                onClick: submit,
                disabled: !isValid || isSubmitting || !isDirty || missingEditor,
                loading: isSubmitting,
                children: formatMessage({ id: isEditing ? 'vaults.form.edit.submit' : 'vaults.form.create.submit' }),
            }}
            dataTestId='vault-form-modal'
        >
            <Heading elementType='h2' color='black'>
                {formatMessage({ id: isEditing ? 'vaults.form.edit.title' : 'vaults.form.create.title' })}
            </Heading>
            <div className={css(felaRules.fields)}>
                <FormField
                    name={VaultFormField.NAME}
                    htmlFor={VaultFormField.NAME}
                    label={<Label>{formatMessage({ id: 'vaults.form.name.label' })}</Label>}
                >
                    <TextInput
                        name={VaultFormField.NAME}
                        id={VaultFormField.NAME}
                        placeholder={formatMessage({ id: 'vaults.form.name.placeholder' })}
                        customStyle={felaRules.nameInput}
                        testId='name-input'
                    />
                </FormField>
                <VaultGroupsField />
                <VaultUsersField />
                {missingEditor ? <FormField validateStatus='error' help={formatMessage({ id: 'vaults.form.error.lastEditor' })} /> : null}
            </div>
        </Modal>
    );
};
